import React, { useEffect, useRef } from 'react';
import { Button, colors } from '@insurancemenu/imenu-components-lib';
import { Done } from '@insurancemenu/imenu-components-lib/dist/icons';
import { Portfolio, ProductBcInfo } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import uniqid from 'uniqid';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import FormsSpinner from 'components/common/general/FormSpinner';
import { useHistory } from 'react-router-dom';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { createStyles } from './styles';

export interface PopularPlanCardProps {
  portfolio: Portfolio;
  advantages: string[];
  planIndex: number;
  portfoliosQuantity: number;
}

interface PlanAccentColors {
  [key: number]: string;
}
// Accent color depends on plan index
const planAccentColors: PlanAccentColors = Object.freeze({
  0: colors.purple[600],
  1: colors.green[600],
  2: colors.yellow[600],
  3: colors.orange[600],
});

const PopularPlanCard = observer((props: PopularPlanCardProps): JSX.Element => {
  const {
    advantages,
    portfolio: { id, name: portfolioName, eStoreTagId, products, productLineId },
    planIndex,
    portfoliosQuantity,
  } = props;

  const {
    popularPlansStore: {
      loadingPortfolioId,
      cart,
      productLineAttrs,
      marketingTags,
      highestAdvantagesRowHeight,
      setHighestAdvantagesRowHeight,
      onAddToProposal,
      onRemoveFromProposal,
      onViewPlanDetails,
    },
  } = useRootStore();

  const advantagesRowRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    if (advantagesRowRef.current) {
      const calculatedHeight = advantagesRowRef.current?.scrollHeight || 0;
      if (calculatedHeight > highestAdvantagesRowHeight) {
        setHighestAdvantagesRowHeight(calculatedHeight);
      }
    }
  }, [highestAdvantagesRowHeight]);

  const history = useHistory();
  const convertedPLineAttrs = productLineAttrs.map((attr) => toJS(attr)).slice();
  const pLineAttributes = convertedPLineAttrs
    .filter((attr) => attr.planTile)
    .filter((attr) => attr.productLine.replaceAll(' ', '') === PRODUCT_LINES_CONTENT_MAP[productLineId].joinedLabel);

  const convertedMarketingTags = marketingTags.map((tag) => toJS(tag)).slice();
  const convertedCart = toJS(cart);

  const cartItem = convertedCart.cartItems.find(({ portfolioId }) => portfolioId === id);
  const addedToProposal = !!cartItem;

  // Show only one first tag on the card
  const currentTagName = convertedMarketingTags.find((tag) => tag.id === eStoreTagId)?.tag || '';
  const currentBc: ProductBcInfo = Object.values(products[0].bcDependentData)[0] as ProductBcInfo;
  const { productAttributes, rates } = currentBc;

  // Styles
  const accentColor = planAccentColors[planIndex] || colors.blue[600];

  const classes = createStyles({
    icon: <Done color={accentColor} />,
    accentColor,
    portfoliosQuantity,
    highestAdvantagesRowHeight,
  });

  const addToProposal = (): void => {
    void onAddToProposal(id, portfolioName);
  };

  const removeFromProposal = (): void => {
    void onRemoveFromProposal(id);
  };

  // Components
  const PlanRates = rates.map(({ displayKey, value }) => {
    return (
      <li className={classes.rateWrapper} key={`${id}-${displayKey}`}>
        <span className={classes.rateName}>{displayKey}</span>
        <span className={classes.rateValue}>${Number(value).toFixed(2)}/mo</span>
      </li>
    );
  });

  const PlanSummary = pLineAttributes.map(({ displayName, id: attrId }) => {
    // get attribute value from the list of all product attributes
    const currentProductAttrValue =
      productAttributes.find(({ attributeId }) => attributeId === attrId)?.displayValue || '';

    return (
      <li className={classes.summaryItem} key={`attr-${attrId}`}>
        <span className={classes.summaryKey}>{displayName}</span>
        <span className={classes.summaryValue}>{currentProductAttrValue}</span>
      </li>
    );
  });

  const PlanAdvantages = advantages.map((value) => {
    return (
      <li key={`advantage-${uniqid()}`} className={classes.advantagesItem}>
        <Done color={accentColor} height="2.4rem" width="2.4rem" />
        {value}
      </li>
    );
  });

  const PlanSection: React.FC<{ children: React.ReactNode }> = ({ children }): JSX.Element => {
    return (
      <div className={classes.section}>
        <h4 className={classes.sectionTitle}>{children}</h4>
      </div>
    );
  };

  return (
    <div className={classes.planCard}>
      <p className={classes.planTag}>{currentTagName}</p>
      <h3 className={classes.planTitle}>{portfolioName}</h3>
      <ul className={classes.rates}>{PlanRates}</ul>
      <PlanSection>Carrier</PlanSection>
      <div className={classes.carrierWrapper}>
        <img src={products[0].carrier.logo} className={classes.carrierLogo} alt="Carrier logo" />
      </div>
      <PlanSection>Key Features</PlanSection>
      <ul className={classes.advantagesList} ref={advantagesRowRef}>
        {PlanAdvantages}
      </ul>
      <PlanSection>Plan Summary</PlanSection>
      <ul className={classes.summaryList}>{PlanSummary}</ul>
      <div className={classes.planButtons}>
        <Button
          type="text"
          size="small"
          color="primary"
          className={classes.planDetailsButton}
          onClick={(): void => {
            onViewPlanDetails(id, convertedCart.id, history.push.bind(history));
          }}
        >
          View plan details
        </Button>
        <Button
          color="custom"
          type={addedToProposal ? 'outlined' : 'contained'}
          customColor={addedToProposal ? colors.red[600] : colors.blue[600]}
          size="large"
          width="22rem"
          onClick={addedToProposal ? removeFromProposal : addToProposal}
        >
          {addedToProposal ? 'Remove From Cart' : 'Add To Cart'}
        </Button>
      </div>
      {loadingPortfolioId === id && <FormsSpinner display />}
    </div>
  );
});

export default PopularPlanCard;
